import { Component, Input, OnInit } from '@angular/core';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-basket-audit',
  templateUrl: './basket-audit.component.html',
  styleUrls: ['./basket-audit.component.scss']
})
export class BasketAuditComponent implements OnInit {

  @Input() storeId: string = '';

  events: any[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';

  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalEvents: number = 0;
  pageSize: number = 50;

  // Filters
  selectedEventType: string = '';
  startDate: string = '';
  endDate: string = '';

  // Event detail modal
  showEventDetails: boolean = false;
  selectedEvent: any = null;

  Math = Math;

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.storeId) {
      this.loadEvents();
    }
  }

  loadEvents(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const params: any = {
      page: this.currentPage,
      limit: this.pageSize
    };

    if (this.selectedEventType) {
      params['eventType'] = this.selectedEventType;
    }
    if (this.startDate) {
      params['startDate'] = this.startDate;
    }
    if (this.endDate) {
      params['endDate'] = this.endDate;
    }

    this.ecommerceService.getBasketAuditLogs(this.storeId, params).subscribe(
      (res: any) => {
        const data = res?.response?.data ?? res?.data ?? null;
        if (data) {
          this.events = data.events || [];
          const pagination = data.pagination || {};
          this.totalEvents = pagination.total ?? pagination.totalEvents ?? 0;
          this.totalPages = pagination.pages ?? pagination.totalPages ?? 1;
          this.currentPage = pagination.page ?? pagination.currentPage ?? 1;
        } else {
          this.events = [];
          console.warn('Unexpected basket audit response format:', res);
        }
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Failed to load basket audit events:', error);
        this.errorMessage = error?.error?.response?.message || 'Failed to load basket audit events';
        this.toastr.error(this.errorMessage);
        this.isLoading = false;
      }
    );
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadEvents();
  }

  resetFilters(): void {
    this.selectedEventType = '';
    this.startDate = '';
    this.endDate = '';
    this.currentPage = 1;
    this.loadEvents();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadEvents();
  }

  viewEventDetails(event: any): void {
    this.selectedEvent = event;
    this.showEventDetails = true;
  }

  closeEventDetails(): void {
    this.showEventDetails = false;
    this.selectedEvent = null;
  }

  getItemsTotal(items: any[]): number {
    if (!items || items.length === 0) {
      return 0;
    }
    return items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unitPrice || 0)), 0);
  }

  formatCurrency(amount: number): string {
    if (!amount && amount !== 0) {
      return '—';
    }
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK'
    }).format(amount);
  }

  formatItems(items: any[]): string {
    if (!items || items.length === 0) {
      return '—';
    }
    return items.map(item => `${item.productName} ×${item.quantity}`).join(', ');
  }

  truncateId(id: string): string {
    if (!id) {
      return '—';
    }
    return id.length > 8 ? id.substring(0, 8) + '...' : id;
  }

  trackByAuditLogId(index: number, event: any): string {
    return event.auditLogId || index;
  }

  getReasonLabel(reason: string | undefined | null): string {
    switch (reason) {
      case 'customer_cancel':
        return 'Customer cancel';
      case 'payment_declined':
        return 'Payment declined';
      case 'staff_action':
        return 'Staff action';
      case 'guest_timeout':
        return 'Guest timeout';
      case 'other':
        return 'Other';
      default:
        return '';
    }
  }

  getReasonBadgeClass(reason: string | undefined | null): string {
    switch (reason) {
      case 'customer_cancel':
        return 'bg-secondary';
      case 'payment_declined':
        return 'bg-warning text-dark';
      case 'staff_action':
        return 'bg-primary';
      case 'guest_timeout':
        return 'bg-info text-dark';
      case 'other':
        return 'bg-light text-dark border';
      default:
        return 'bg-light text-muted border';
    }
  }

  /** Backend may expose any of these field names once theft-check enrichment ships. */
  isNotPaidWithin15Min(event: any): boolean | null {
    if (event?.notPaidWithin15Min === true || event?.itemsNotPaidWithin15Min === true) {
      return true;
    }
    if (event?.notPaidWithin15Min === false || event?.itemsNotPaidWithin15Min === false) {
      return false;
    }
    return null;
  }

  shouldShowClearRemoveBadges(event: any): boolean {
    return event?.eventType === 'clear_cart' || event?.eventType === 'remove_item';
  }
}
